import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import useOnlineStatus from './useOnlineStatus';
import {
  listPendentesSync, marcarSincronizando, marcarErroSync, marcarPendenteNovamente,
  removerDaFila, contarPendentesSync,
} from '../services/offlineDb';

// PDV offline (Fase 1): dispara sozinho quando a conexão volta e tenta
// reenviar, em ordem, cada venda que ficou na fila local. Cada entrada já
// carrega seu `chave_idempotencia` (uuid gerado no momento da venda) —
// reenviar a mesma entrada mais de uma vez é seguro (vendasController.criar
// devolve a venda já criada em vez de duplicar).
export default function useSyncQueue() {
  const online = useOnlineStatus();
  const [pendentesCount, setPendentesCount] = useState(0);
  const [sincronizando, setSincronizando] = useState(false);
  const emVooRef = useRef(false);

  const atualizarContagem = useCallback(async () => {
    setPendentesCount(await contarPendentesSync());
  }, []);

  const sincronizarAgora = useCallback(async () => {
    if (emVooRef.current) return;
    emVooRef.current = true;
    setSincronizando(true);
    try {
      const fila = await listPendentesSync();
      for (const entrada of fila) {
        try {
          await marcarSincronizando(entrada.uuid);
          await api.post('/vendas', entrada.payload);
          await removerDaFila(entrada.uuid);
        } catch (err) {
          if (err.response) {
            // Resposta real do servidor (validação falhou de verdade —
            // ex: estoque ficou insuficiente, limite de crédito estourado
            // enquanto a venda ficou pendente) — para de tentar sozinho,
            // fica visível no painel pro gerente revisar manualmente.
            const msg = err.response.data?.erro || 'Erro ao sincronizar.';
            await marcarErroSync(entrada.uuid, msg);
          } else {
            // Sem resposta do servidor (rede caiu nesse meio tempo, ou
            // caiu de novo) — volta pra 'pendente' e para o lote inteiro:
            // as próximas entradas também falhariam do mesmo jeito, sem
            // sentido gastar tentativa de cada uma agora. Próximo ciclo
            // (reconexão) retoma dessa mesma entrada em diante.
            await marcarPendenteNovamente(entrada.uuid);
            await atualizarContagem();
            break;
          }
        }
        await atualizarContagem();
      }
    } finally {
      setSincronizando(false);
      emVooRef.current = false;
    }
  }, [atualizarContagem]);

  useEffect(() => { atualizarContagem(); }, [atualizarContagem]);

  useEffect(() => {
    if (online) sincronizarAgora();
  }, [online, sincronizarAgora]);

  return { pendentesCount, sincronizando, sincronizarAgora, atualizarContagem };
}
